import React, { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import {
  AlertTriangle, Building2, Search, Users, Briefcase, Loader2, RefreshCw,
} from 'lucide-react';
import ReportRecipientsManager from '../ReportRecipientsManager';

const ROLE_TINT = {
  employer: 'bg-indigo-100 text-indigo-700',
  agent:    'bg-emerald-100 text-emerald-700',
};

export default function AdminReportRecipientsPanel() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiCall('/api/admin/orgs');
      setOrgs(res.orgs || []);
    } catch (e) {
      toast.error('Failed to load orgs', { description: e.message });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = orgs.filter((o) => {
    if (roleFilter !== 'all' && o.role !== roleFilter) return false;
    const s = search.trim().toLowerCase();
    if (!s) return true;
    return `${o.full_name} ${o.email} ${o.company_name || ''}`.toLowerCase().includes(s);
  });

  return (
    <div className="space-y-6" data-testid="admin-report-recipients">
      {/* Header */}
      <div className="bg-gradient-to-r from-rose-600 via-orange-600 to-amber-500 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-2xl font-bold tracking-tight">Report Settings</h2>
              <p className="text-sm text-white/85">Configure incident-report HR / Supervisor / On-Call recipients on behalf of any agency or employer.</p>
            </div>
          </div>
          <button
            onClick={load}
            className="p-2.5 rounded-xl bg-white/15 hover:bg-white/25 backdrop-blur-md ring-1 ring-white/20"
            title="Refresh"
            data-testid="admin-rr-refresh"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Org list */}
        <div className="lg:col-span-4 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col" style={{ minHeight: '500px' }}>
          <div className="p-3 border-b border-slate-100 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search org / contact / email"
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                data-testid="admin-rr-search"
              />
            </div>
            <div className="flex gap-1.5">
              {[
                { id: 'all', label: 'All', count: orgs.length },
                { id: 'employer', label: 'Employers', count: orgs.filter((o) => o.role === 'employer').length },
                { id: 'agent', label: 'Agencies', count: orgs.filter((o) => o.role === 'agent').length },
              ].map((f) => (
                <button
                  key={f.id}
                  onClick={() => setRoleFilter(f.id)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition ${
                    roleFilter === f.id ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`admin-rr-filter-${f.id}`}
                >
                  {f.label} <span className="opacity-70">{f.count}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
            {loading ? (
              <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>
            ) : filtered.length === 0 ? (
              <p className="text-center text-sm text-slate-400 py-12">No matching orgs</p>
            ) : (
              filtered.map((o) => {
                const Icon = o.role === 'agent' ? Users : Briefcase;
                const isSelected = selected?._id === o._id;
                return (
                  <button
                    key={o._id}
                    onClick={() => setSelected(o)}
                    className={`w-full text-left px-3 py-3 transition flex items-center gap-2.5 ${
                      isSelected ? 'bg-rose-50 ring-1 ring-inset ring-rose-200' : 'hover:bg-slate-50'
                    }`}
                    data-testid={`admin-rr-org-${o._id}`}
                  >
                    <div className={`p-1.5 rounded-md ${ROLE_TINT[o.role] || 'bg-slate-100 text-slate-600'} flex-shrink-0`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-sm text-slate-900 truncate">{o.company_name || o.full_name}</p>
                      <p className="text-[11px] text-slate-500 truncate">{o.email}</p>
                    </div>
                    <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${ROLE_TINT[o.role]}`}>{o.role}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Recipients editor */}
        <div className="lg:col-span-8">
          {selected ? (
            <ReportRecipientsManager
              key={selected._id}
              orgId={selected._id}
              viewLabel={`${selected.company_name || selected.full_name} · ${selected.role}`}
            />
          ) : (
            <div className="bg-white rounded-2xl border border-dashed border-slate-300 p-12 text-center" style={{ minHeight: '500px' }}>
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="text-base font-bold text-slate-700">Select an org</h3>
              <p className="text-sm text-slate-500">Pick an employer or agency from the list to view and edit their report recipients.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
